import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  FurtherConsiderations,
  PanelField,
  Section,
} from '../models';
import { adrenalGauge } from '../models/StressAdrenalAnalysis';
import { AIExportSerializer } from './AIExportSerializer';
import { WBCEvaluationHelper } from './WBCEvaluationHelper';
import { GlucoseRegulationInsights } from '../components/bloodworkz/glucose-regulation-insights';
import { ThyroidRegulationInsights } from '../components/bloodworkz/thyroid-regulation-insights';
import {
  GROUPS,
  SEVERITY,
  Status,
  getSectionStatus,
  bloodOxygenStatus,
} from '../components/bloodworkz/results-overview-panel';
import { AI_REVIEW_PROMPT } from './aiReviewPrompt';

// All sections whose tab content is emitted, in the order used by the export.
const ALL_SECTIONS = [
  Section.RBC, Section.WBC, Section.IRON, Section.PLATELETS,
  Section.GLUCOSE, Section.KIDNEY, Section.NUTRIENTS, Section.ELECTROLYTES,
  Section.PROTEINS, Section.LIVER_BILIARY, Section.LIPIDS,
  Section.INFLAMMATION_OXIDATIVE_STRESS, Section.THYROID,
  Section.STRESS_ADRENAL, Section.SEX_HORMONE, Section.PROSTATE,
];

// Maps each internal tab name (as stored on Consideration.tab) to the key used
// in the export. Supplements are intentionally OMITTED here so no supplement
// content ever reaches the raw-data file. About and References are handled
// separately below (they need per-marker / tag-resolution logic).
const TAB_KEY_MAP: Record<string, string> = {
  Interpretation:   'Insights',
  Causes:           'PossibleRootCauses',
  Testing:          'AdditionalTesting',
  Considerations:   'FurtherConsiderations',
  Symptoms:         'SignsAndSymptoms',
  Diet:             'Diet',
  Lifestyle:        'Lifestyle',
};

// Maps each Section to the BloodworkAnalysis getter that returns the panel
// markers displayed for it — mirrors how the UI feeds panelMarkers to each
// section's tabs (and thus the About tab). Explicit because the model has no
// generic section→markers dispatcher.
const SECTION_MARKER_GETTERS: Record<string, string> = {
  [Section.RBC]:                          'getRBCPanelMarkers',
  [Section.WBC]:                          'getWBCPanelMarkers',
  [Section.IRON]:                         'getIronPanelMarkers',
  [Section.PLATELETS]:                    'getPlateletPanelMarkers',
  [Section.GLUCOSE]:                      'getGlucosePanelMarkers',
  [Section.KIDNEY]:                       'getKidneyPanelMarkers',
  [Section.NUTRIENTS]:                    'getMineralPanelMarkers',
  [Section.ELECTROLYTES]:                 'getElectrolytePanelMarkers',
  [Section.PROTEINS]:                     'getProteinsPanelMarkers',
  [Section.LIVER_BILIARY]:                'getLiverPanelMarkers',
  [Section.LIPIDS]:                       'getLipidsPanelMarkers',
  [Section.INFLAMMATION_OXIDATIVE_STRESS]:'getInflammationOxidativeStressPanelMarkers',
  [Section.THYROID]:                      'getThyroidPanelMarkers',
  [Section.STRESS_ADRENAL]:               'getStressAdrenalPanelMarkers',
  [Section.SEX_HORMONE]:                  'getSexHormonePanelMarkers',
  [Section.PROSTATE]:                     'getProstatePanelMarkers',
};

function sectionPanelMarkers(ba: BloodworkAnalysis, section: string): any[] {
  const getterName = SECTION_MARKER_GETTERS[section];
  const getter = getterName ? (ba as any)[getterName] : undefined;
  return typeof getter === 'function' ? getter.call(ba) || [] : [];
}

// Gauge-label prefix per adrenal condition (mirrors CONDITION_LABEL in
// stress-adrenal-regulation-insights.tsx).
const ADRENAL_CONDITION_LABEL: Record<string, string> = {
  insufficiency: 'Adrenal Insufficiency',
  excess: 'Adrenal Excess',
  balanced: 'Adrenal Dysfunction',
};

// Serializable view of the RBC "Anemia Status" insight panel, mirroring what
// bloodwork-anemia-status.tsx renders (gauge + RBC size/color + the conditional
// text lines). Returns null when the RBC section has no markers to show.
function buildAnemiaInsights(ba: BloodworkAnalysis): any | null {
  if (sectionPanelMarkers(ba, Section.RBC).length === 0) return null;
  const anemia: any = {
    label: 'Suboptimal Blood Oxygen Delivery is',
    category: ba.anemicProbabilityCategory,
    probability: ba.anemicProbability,
    redBloodCellSize: ba.redBloodCellSize
      ? { type: ba.redBloodCellSize.type, name: ba.redBloodCellSize.name }
      : undefined,
    redBloodCellColor: ba.redBloodCellColor
      ? { type: ba.redBloodCellColor.type, name: ba.redBloodCellColor.name }
      : undefined,
  };
  if (ba.nutrientDeficiencyOrInflammationLikelyText) {
    anemia.nutrientDeficiencyOrInflammationLikelyText =
      ba.nutrientDeficiencyOrInflammationLikelyText;
  }
  if (ba.boneMarrowAnalysis?.boneMarrowOverproductionIsLikely) {
    anemia.boneMarrowOverproductionIsLikely = true;
  }
  const rootCauses = ba.possibleRootCauses ? ba.possibleRootCauses() : [];
  if (rootCauses && rootCauses.length > 0) {
    anemia.possibleRootCauses = rootCauses;
  }
  if (ba.isAnyDehydrationPossibleOrHigher?.()) {
    anemia.dehydrationText = ba.deriveDehydrationText();
  }
  if (ba.isPoorOxygenationPossibleOrHigher?.()) {
    anemia.poorOxygenationText = ba.derivePoorOxygenationText();
  }
  if (ba.isPoorOxygenationDueToSmoking?.()) {
    anemia.poorOxygenationSmokerText = ba.derivePoorOxygenationTextForSmoker();
  }
  return anemia;
}

// Markers Outside Optimal (MOO) — the same 6-bucket grouping the UI/PDF show,
// so the AI can reference flagged markers by group without the export (or the AI)
// re-listing every value. Mirrors the filters in bloodwork-test-analysis.tsx.
// Returns readable short labels per bucket; empty buckets are omitted.
function buildMarkersOutsideOptimal(ba: BloodworkAnalysis): any | null {
  const seen = new Set<any>();
  const all: any[] = [];
  Object.keys(SECTION_MARKER_GETTERS).forEach(section => {
    sectionPanelMarkers(ba, section).forEach(pm => {
      if (pm && !seen.has(pm)) {
        seen.add(pm);
        all.push(pm);
      }
    });
  });

  const label = (pm: any) => pm.field?.shortLabel || pm.field?.label || pm.attribute;
  const higherIsBetter = (pm: any) => pm.isEGFR?.() || pm.field?.useEGFRChart;
  const isRdw = (pm: any) => pm.field?.useRDWChart;
  const egfrBelowTier = (pm: any): number => {
    const r = pm.range;
    if (!r) return 3;
    if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
    if (r.labMin > 0 && pm.value < r.labMin) return 2;
    return 3;
  };
  const isHighSide = (pm: any) =>
    !higherIsBetter(pm) &&
    (pm.isGreaterThanAlarm?.() || pm.isGreaterThanLab?.() || pm.isGreaterThanOptimal?.());

  const withValue = all.filter(pm => pm.hasValue?.());
  const needsAttention = withValue.filter(pm =>
    higherIsBetter(pm)
      ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
      : pm.isGreaterThanAlarm?.() || (!isRdw(pm) && pm.isLessThanAlarm?.()),
  );
  const worthDiscussing = withValue.filter(pm =>
    higherIsBetter(pm)
      ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
      : (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
        (!isRdw(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.()),
  );
  const fineTune = withValue.filter(pm =>
    higherIsBetter(pm)
      ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
      : (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
        (!isRdw(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.()),
  );

  const buckets: Record<string, string[]> = {
    highAlarm: needsAttention.filter(isHighSide).map(label),
    lowAlarm: needsAttention.filter(pm => !isHighSide(pm)).map(label),
    aboveLab: worthDiscussing.filter(isHighSide).map(label),
    belowLab: worthDiscussing.filter(pm => !isHighSide(pm)).map(label),
    aboveOptimal: fineTune.filter(isHighSide).map(label),
    belowOptimal: fineTune.filter(pm => !isHighSide(pm)).map(label),
  };

  const result: Record<string, string[]> = {};
  Object.keys(buckets).forEach(k => {
    if (buckets[k].length > 0) result[k] = buckets[k];
  });
  return Object.keys(result).length > 0 ? result : null;
}

// Friendly section labels shown in the Results Overview panel (RBC → "Blood
// Oxygen", WBC → "Immune", etc.). Mirrors buildSections() in
// results-overview-panel.tsx.
const SECTION_LABELS: Record<string, string> = {
  [Section.RBC]: 'Blood Oxygen',
  [Section.IRON]: 'Iron Status',
  [Section.WBC]: 'Immune',
  [Section.PLATELETS]: 'Blood Clotting',
  [Section.GLUCOSE]: 'Glucose',
  [Section.KIDNEY]: 'Kidney',
  [Section.PROTEINS]: 'Proteins',
  [Section.NUTRIENTS]: 'Vitamins & Minerals',
  [Section.ELECTROLYTES]: 'Electrolytes',
  [Section.LIVER_BILIARY]: 'Liver',
  [Section.LIPIDS]: 'Lipids',
  [Section.INFLAMMATION_OXIDATIVE_STRESS]: 'Inflammation',
  [Section.THYROID]: 'Thyroid',
  [Section.STRESS_ADRENAL]: 'Stress & Adrenal',
  [Section.SEX_HORMONE]: 'Hormones',
  [Section.PROSTATE]: 'Prostate',
};

// Section markers as the Results Overview panel sees them — the Stress & Adrenal
// card excludes Na/K (those drive the adrenal gauge instead).
function resultsOverviewMarkers(ba: BloodworkAnalysis, section: string): any[] {
  const markers = sectionPanelMarkers(ba, section);
  if (section === Section.STRESS_ADRENAL) {
    return markers.filter(
      (pm: any) => pm.attribute !== 'sodium' && pm.attribute !== 'potassium',
    );
  }
  return markers;
}

// Results Overview: the per-section color the practitioner sees, plus a rollup
// into the 5 area groups (Fuel & Energize, etc.) with a per-system score. Gives
// the AI a high-level status for EVERY body system — including ones with no
// detailed pattern engine. Colors and grouping reuse results-overview-panel.tsx
// so this stays in lockstep with the UI; the per-system score is new (there is
// none in the UI): 0–100, higher = healthier (green 100 → red 0), plus the
// worst section color in the group (worst-wins, matching the panel).
function buildResultsOverview(ba: BloodworkAnalysis): any | null {
  const sections: Record<string, { label: string; color: Status }> = {};
  const colorOf: Record<string, Status> = {};

  Object.keys(SECTION_MARKER_GETTERS).forEach(section => {
    const markers = resultsOverviewMarkers(ba, section);
    if (!markers.some((pm: any) => pm.hasValue?.())) return; // untested → skip
    const color =
      section === Section.RBC
        ? bloodOxygenStatus(ba, markers)
        : getSectionStatus(markers, section as any);
    sections[section] = { label: SECTION_LABELS[section] || section, color };
    colorOf[section] = color;
  });

  if (Object.keys(sections).length === 0) return null;

  const systems = GROUPS.map(group => {
    const tested = group.sectionKeys.filter(s => colorOf[s] !== undefined);
    if (tested.length === 0) {
      return {
        name: group.name,
        subtitle: group.subtitle,
        sections: [],
        worstColor: 'notAssessed',
        score: null,
      };
    }
    const avgSeverity =
      tested.reduce((sum, s) => sum + SEVERITY[colorOf[s]], 0) / tested.length;
    const score = Math.round((avgSeverity / 3) * 100);
    let worst = tested[0];
    tested.forEach(s => {
      if (SEVERITY[colorOf[s]] < SEVERITY[colorOf[worst]]) worst = s;
    });
    return {
      name: group.name,
      subtitle: group.subtitle,
      sections: tested,
      worstColor: colorOf[worst],
      score,
    };
  });

  return { sections, systems };
}

export interface AIExportData {
  // AI-review prompt, placed first so it sits at the TOP of the exported file.
  instructions: string;
  analysisHistory: any[];
}

/**
 * Build the raw-data export object for the "Export Raw Data" feature. Pure and
 * side-effect free (no DOM / download) so it can be unit-tested; the component's
 * exportJson() just serializes and downloads what this returns.
 *
 * Per test it includes:
 *  - serialized markers + ranges (the Reference Range tab / marker table);
 *  - every insight panel: RBC/anemia (anemiaInsights), immune (wbcEvaluation +
 *    immuneGauge), glucose (glucosePatterns + glucoseGauge), thyroid
 *    (thyroidInsights), and stress/adrenal (adrenalGauge);
 *  - per-section tab content (tabsContent) for EVERY displayed tab except
 *    Supplements — Insights, PossibleRootCauses, AdditionalTesting,
 *    FurtherConsiderations, SignsAndSymptoms, Diet, Lifestyle, References
 *    (citations resolved), and About (per-marker descriptions);
 *  - every practitioner note across all sections (notes).
 */
export function buildAIExportData(
  bloodworks: Bloodwork[],
  bloodworkRanges: BloodworkRange[],
  furtherConsiderations: FurtherConsiderations,
): AIExportData {
  const bloodworkAnalysisHistory = bloodworks.map(bw => {
    const ba = new BloodworkAnalysis({
      bloodwork: bw,
      bloodworkRanges,
      bloodworkFields: PanelField.ALL_FIELDS,
    });
    ba.init().performAnalysis();
    return ba;
  });

  return {
    // First key → serializes at the top of the file, so the AI reads it first.
    instructions: AI_REVIEW_PROMPT,
    analysisHistory: bloodworkAnalysisHistory.map(ba => {
      const jsonString = JSON.stringify(ba, AIExportSerializer.replacer);
      const json = JSON.parse(jsonString);

      // Add WBC (immune) pattern data
      const wbcEvaluation = WBCEvaluationHelper.evaluate(ba);
      if (Object.keys(wbcEvaluation).length > 0) {
        (json as any).wbcEvaluation = wbcEvaluation;
      }

      // Immune gauge likelihood (mirrors the Immune Activity Insights panel)
      if (ba.wbc?.hasValue?.()) {
        (json as any).immuneGauge = {
          label: 'Immune System Abnormality is',
          category: ba.wbcAnalysis?.immuneSystemDisorderProbabilityCategory,
          probability: ba.wbcAnalysis?.immuneSystemDisorderProbability,
        };
      }

      // Add Glucose Regulation pattern insights (mirrors the panel; no supplements)
      const glucosePatterns = GlucoseRegulationInsights.exportPatterns(ba);
      if (glucosePatterns.length > 0) {
        (json as any).glucosePatterns = glucosePatterns;
      }

      // Glucose gauge likelihood (mirrors the Glucose Regulation Insights panel)
      if (GlucoseRegulationInsights.shouldRender(ba)) {
        (json as any).glucoseGauge = {
          label: 'Glucose Dysregulation is',
          category: ba.glucoseAnalysis?.glucoseRegulationProbabilityCategory,
          probability: ba.glucoseAnalysis?.glucoseRegulationProbability,
        };
      }

      // RBC / Anemia Status insight panel
      const anemiaInsights = buildAnemiaInsights(ba);
      if (anemiaInsights) {
        (json as any).anemiaInsights = anemiaInsights;
      }

      // Thyroid Insights panel (5-area bands + resolved pattern)
      const thyroidInsights = ThyroidRegulationInsights.exportInsights(ba);
      if (thyroidInsights) {
        (json as any).thyroidInsights = thyroidInsights;
      }

      // Stress & Adrenal Insights gauge (Na/K primary; cortisol/DHEA-S/pregnenolone refine)
      const adrenal = adrenalGauge(ba.sodiumPotassiumRatio, {
        cortisolAM: ba.cortisolAM,
        cortisolPM: ba.cortisolPM,
        dheaS: ba.dheaS,
        pregnenolone: ba.pregnenolone,
      });
      if (adrenal) {
        (json as any).adrenalGauge = {
          label: `${ADRENAL_CONDITION_LABEL[adrenal.condition]} is`,
          condition: adrenal.condition,
          category: adrenal.category,
          probability: adrenal.probability,
          markers: adrenal.markers,
        };
      }

      // Markers Outside Optimal (MOO) — flagged markers grouped by severity,
      // so the summary can reference them without re-listing every value.
      const markersOutsideOptimal = buildMarkersOutsideOptimal(ba);
      if (markersOutsideOptimal) {
        (json as any).markersOutsideOptimal = markersOutsideOptimal;
      }

      // Results Overview — per-section color + the 5 area-group rollup with a
      // per-system score. High-level status for every body system.
      const resultsOverview = buildResultsOverview(ba);
      if (resultsOverview) {
        (json as any).resultsOverview = resultsOverview;
      }

      // Every practitioner note across all sections (body-system sections plus
      // Results Overview, the general Notes tab, BMI, etc.), keyed by section.
      const notes: Record<string, string> = {};
      (ba.bloodwork.bloodworkNotes?.notes || []).forEach((n: any) => {
        if (n?.text?.trim()) {
          notes[n.section] = n.text.trim();
        }
      });
      if (Object.keys(notes).length > 0) {
        (json as any).notes = notes;
      }

      // Build tab content for every section that has root-cause categories
      const tabsContent: Record<string, any> = {};

      ALL_SECTIONS.forEach(section => {
        const categories = ba.possibleRootCauseCategoriesFor
          ? ba.possibleRootCauseCategoriesFor(section) || []
          : [];

        const sectionContent: Record<string, any> = {};

        Object.entries(TAB_KEY_MAP).forEach(([internalTab, exportKey]) => {
          const considerations: Array<{ title: string; items: string[] }> = [];
          categories.forEach(cat => {
            furtherConsiderations
              .findConsiderations(cat.rootCauseCategory, section, internalTab)
              .forEach(c => considerations.push({ title: c.title, items: c.items }));
          });
          if (considerations.length > 0) {
            sectionContent[exportKey] = considerations;
          }
        });

        // References tab — items are Reference Library tag slugs; resolve each to
        // its full citation (findByTag -> library row) so the export carries
        // readable citations, not opaque slugs. Falls back to the raw item when a
        // tag has no library row (legacy inline citations).
        const references: Array<{ title: string; items: string[] }> = [];
        categories.forEach(cat => {
          furtherConsiderations
            .findConsiderations(cat.rootCauseCategory, section, 'References')
            .forEach(c => {
              const items = (c.items || []).map(item => {
                const libraryRow = furtherConsiderations.findByTag(item);
                return libraryRow?.items?.[0] ?? item;
              });
              references.push({ title: c.title, items });
            });
        });
        if (references.length > 0) {
          sectionContent.References = references;
        }

        // About tab — per-marker plain-language descriptions, resolved exactly
        // as the About panel does (findAboutConsideration keyed off each
        // displayed marker's field id), deduped by categoryId.
        const aboutSeen: Record<number, boolean> = {};
        const about: Array<{ title: string; items: string[] }> = [];
        sectionPanelMarkers(ba, section).forEach((pm: any) => {
          if (!pm || !pm.field || (pm.hasValue && !pm.hasValue())) return;
          const a = furtherConsiderations.findAboutConsideration(section, pm.field.id);
          if (a && a.items && a.items.length > 0 && !aboutSeen[a.categoryId]) {
            aboutSeen[a.categoryId] = true;
            about.push({ title: a.title, items: a.items });
          }
        });
        if (about.length > 0) {
          sectionContent.About = about;
        }

        if (Object.keys(sectionContent).length > 0) {
          tabsContent[section] = sectionContent;
        }
      });

      if (Object.keys(tabsContent).length > 0) {
        (json as any).tabsContent = tabsContent;
      }

      return json;
    }),
  };
}
